/**
 * Knowledge → Articles (9 Oct): every article, searchable and filtered by
 * status, area, tag, owner, source, review due and index state. Read-only
 * for IT; editors also get "New article", "Retry indexing" and the exports.
 */

import { Download, Icon, Plus, RefreshCw } from "@norican/icons";
import {
  Alert,
  Badge,
  Button,
  DataGrid,
  type DataGridColumn,
  Field,
  Input,
  Typography,
} from "@norican/react";
import { useMemo } from "react";
import { Link, useSearchParams } from "react-router";

import {
  AppBreadcrumbs,
  EmptyStateBlock,
  ErrorState,
  LoadingRows,
  PageContainer,
  RelativeTime,
} from "@/components/common";
import { useDocumentTitle } from "@/hooks";
import { useT } from "@/i18n";
import type { KnowledgeArticleSummary } from "@/lib/api";

import {
  KNOWLEDGE_EXPORT_XLSX,
  KNOWLEDGE_EXPORT_ZIP,
  useKnowledgeAreas,
  useKnowledgeArticles,
  useReindexKnowledge,
} from "./api";
import { areaNames, areaOptions, type ListFilters } from "./lib/knowledge";
import {
  AudienceBadge,
  IndexBadge,
  KnowledgeGate,
  OptionSelect,
  ReviewBadge,
  StatusBadge,
} from "./shared";

const FILTER_KEYS = [
  "q",
  "status",
  "area",
  "tag",
  "owner",
  "source_type",
  "audience",
  "review",
  "index",
] as const;

export function KnowledgeArticlesPage() {
  const { t } = useT();
  useDocumentTitle(t("knowledgeAdmin.articles.title"));
  return (
    <KnowledgeGate title={t("knowledgeAdmin.articles.title")}>
      <ArticlesBody />
    </KnowledgeGate>
  );
}

function ArticlesBody() {
  const { t } = useT();
  const [params, setParams] = useSearchParams();
  const filters: ListFilters = Object.fromEntries(
    FILTER_KEYS.map((key) => [key, params.get(key) ?? ""]),
  );
  const list = useKnowledgeArticles(filters);
  const areas = useKnowledgeAreas();
  const reindex = useReindexKnowledge();
  const areaList = useMemo(() => areas.data?.items ?? [], [areas.data]);

  function set(key: (typeof FILTER_KEYS)[number], value: string) {
    const next = new URLSearchParams(params);
    if (value === "") {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    setParams(next, { replace: true });
  }

  const data = list.data;
  const columns = useMemo<DataGridColumn<KnowledgeArticleSummary>[]>(
    () => [
      {
        id: "title",
        header: t("knowledgeAdmin.field.title"),
        width: 360,
        sortable: true,
        accessor: (row) => row.title,
        cell: (row) => (
          <span className="flex flex-col gap-0.5">
            <Link
              to={`/knowledge/articles/${row.id}`}
              className="text-accent-fg-subtle font-medium hover:underline"
            >
              {row.title}
            </Link>
            <span className="text-fg-muted text-xs">
              {row.id} ·{" "}
              {areaNames(row.areas ?? [], areaList).join(", ") || (row.tags ?? []).join(", ")}
            </span>
          </span>
        ),
      },
      {
        id: "status",
        header: t("knowledgeAdmin.field.status"),
        width: 200,
        sortable: true,
        accessor: (row) => row.status,
        cell: (row) => (
          <span className="flex flex-wrap gap-1">
            <StatusBadge status={row.status} />
            <ReviewBadge state={row.review_state} />
            {row.status === "published" ? <IndexBadge state={row.index_state} /> : null}
            <AudienceBadge audience={row.audience} />
          </span>
        ),
      },
      {
        id: "owner",
        header: t("knowledgeAdmin.field.owner"),
        width: 180,
        sortable: true,
        accessor: (row) => row.owner_name ?? row.owner_email ?? "",
      },
      {
        id: "source",
        header: t("knowledgeAdmin.field.source"),
        width: 150,
        accessor: (row) => t(`knowledgeAdmin.source.${row.source_type}`),
      },
      {
        id: "updated",
        header: t("knowledgeAdmin.field.updated"),
        width: 150,
        sortable: true,
        accessor: (row) => row.updated_at,
        cell: (row) => <RelativeTime value={row.updated_at} />,
      },
    ],
    [areaList, t],
  );

  const statusOptions = ["draft", "published", "retired"].map((value) => ({
    value,
    label: t(`knowledgeAdmin.status.${value as "draft"}`),
  }));
  const sourceOptions = ["manual", "upload", "sharepoint_export", "ivanti_ticket", "import"].map(
    (value) => ({ value, label: t(`knowledgeAdmin.source.${value as "manual"}`) }),
  );
  const areaChoices = [
    { value: "none", label: t("knowledgeAdmin.areas.withoutArea") },
    ...areaOptions(areaList).map((o) => ({
      value: o.value,
      label: o.label,
    })),
  ];
  const any = { value: "", label: t("knowledgeAdmin.filter.any") };

  return (
    <PageContainer
      title={t("knowledgeAdmin.articles.title")}
      subtitle={t("knowledgeAdmin.articles.subtitle")}
      breadcrumbs={
        <AppBreadcrumbs
          items={[
            { label: t("knowledgeAdmin.overview.title"), to: "/knowledge" },
            { label: t("knowledgeAdmin.articles.title") },
          ]}
        />
      }
      width="wide"
      actions={
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            startIcon={<Icon icon={Download} size="sm" />}
            render={<a href={KNOWLEDGE_EXPORT_ZIP} download />}
          >
            {t("knowledgeAdmin.articles.exportZip")}
          </Button>
          <Button
            variant="outline"
            startIcon={<Icon icon={Download} size="sm" />}
            render={<a href={KNOWLEDGE_EXPORT_XLSX} download />}
          >
            {t("knowledgeAdmin.articles.exportXlsx")}
          </Button>
          {data?.can_edit ? (
            <Button
              startIcon={<Icon icon={Plus} size="sm" />}
              render={<Link to="/knowledge/articles/new" />}
            >
              {t("knowledgeAdmin.articles.new")}
            </Button>
          ) : null}
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {data && data.not_indexed > 0 ? (
          <Alert tone="warning" title={t("knowledgeAdmin.articles.notIndexedTitle")}>
            <div className="flex flex-wrap items-center gap-3">
              <span>{t("knowledgeAdmin.articles.notIndexed", { count: data.not_indexed })}</span>
              {data.can_edit ? (
                <Button
                  size="sm"
                  variant="outline"
                  loading={reindex.isPending}
                  startIcon={<Icon icon={RefreshCw} size="sm" />}
                  onClick={() => reindex.mutate()}
                >
                  {t("knowledgeAdmin.articles.retryIndexing")}
                </Button>
              ) : null}
            </div>
          </Alert>
        ) : null}

        <div className="grid gap-3 md:grid-cols-4">
          <Field name="knowledge-q" label={t("knowledgeAdmin.filter.search")}>
            <Input
              type="search"
              value={filters.q ?? ""}
              onChange={(event) => set("q", event.target.value)}
              placeholder={t("knowledgeAdmin.filter.searchPlaceholder")}
              autoComplete="off"
            />
          </Field>
          <Field name="knowledge-status" label={t("knowledgeAdmin.field.status")}>
            <OptionSelect
              options={[any, ...statusOptions]}
              value={filters.status ?? ""}
              onValueChange={(v) => set("status", v)}
              placeholder={t("knowledgeAdmin.filter.any")}
            />
          </Field>
          <Field name="knowledge-area" label={t("knowledgeAdmin.field.areas")}>
            <OptionSelect
              options={[any, ...areaChoices]}
              value={filters.area ?? ""}
              onValueChange={(v) => set("area", v)}
              placeholder={t("knowledgeAdmin.filter.any")}
            />
          </Field>
          <Field name="knowledge-review" label={t("knowledgeAdmin.field.review")}>
            <OptionSelect
              options={[
                any,
                { value: "due", label: t("knowledgeAdmin.filter.reviewDue") },
                { value: "overdue", label: t("knowledgeAdmin.review.overdue") },
              ]}
              value={filters.review ?? ""}
              onValueChange={(v) => set("review", v)}
              placeholder={t("knowledgeAdmin.filter.any")}
            />
          </Field>
          <Field name="knowledge-tag" label={t("knowledgeAdmin.field.tags")}>
            <OptionSelect
              options={[
                any,
                ...(data?.tags ?? []).map((tag) => ({
                  value: tag.tag,
                  label: `${tag.tag} (${tag.count})`,
                })),
              ]}
              value={filters.tag ?? ""}
              onValueChange={(v) => set("tag", v)}
              placeholder={t("knowledgeAdmin.filter.any")}
            />
          </Field>
          <Field name="knowledge-owner" label={t("knowledgeAdmin.field.owner")}>
            <OptionSelect
              options={[any, ...(data?.owners ?? []).map((o) => ({ value: o, label: o }))]}
              value={filters.owner ?? ""}
              onValueChange={(v) => set("owner", v)}
              placeholder={t("knowledgeAdmin.filter.any")}
            />
          </Field>
          <Field name="knowledge-source" label={t("knowledgeAdmin.field.source")}>
            <OptionSelect
              options={[any, ...sourceOptions]}
              value={filters.source_type ?? ""}
              onValueChange={(v) => set("source_type", v)}
              placeholder={t("knowledgeAdmin.filter.any")}
            />
          </Field>
          <Field name="knowledge-index" label={t("knowledgeAdmin.field.index")}>
            <OptionSelect
              options={[any, { value: "not_indexed", label: t("knowledgeAdmin.index.notIndexed") }]}
              value={filters.index ?? ""}
              onValueChange={(v) => set("index", v)}
              placeholder={t("knowledgeAdmin.filter.any")}
            />
          </Field>
        </div>

        {list.isLoading ? (
          <LoadingRows rows={8} />
        ) : list.error != null || data === undefined ? (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <Typography variant="body-sm" tone="muted">
                {t("knowledgeAdmin.articles.count", { count: data.total })}
              </Typography>
              {(["draft", "published", "retired"] as const).map((status) => (
                <Badge key={status} tone="neutral" variant="outline" size="sm">
                  {t(`knowledgeAdmin.status.${status}`)}: {data.status_counts[status] ?? 0}
                </Badge>
              ))}
            </div>
            {data.items.length === 0 ? (
              <EmptyStateBlock
                title={t("knowledgeAdmin.articles.empty")}
                description={data.can_edit ? t("knowledgeAdmin.articles.emptyHint") : null}
                actions={
                  data.can_edit ? (
                    <Button render={<Link to="/knowledge/bulk-add" />}>
                      {t("knowledgeAdmin.nav.bulkAdd")}
                    </Button>
                  ) : undefined
                }
              />
            ) : (
              <DataGrid
                aria-label={t("knowledgeAdmin.articles.title")}
                columns={columns}
                data={data.items}
                getRowId={(row) => row.id}
                tableLayout="fixed"
                density="compact"
              />
            )}
          </>
        )}
      </div>
    </PageContainer>
  );
}
